import Phaser from 'phaser';
import { CROSSBOW } from '../../config';
import { Enemy } from '../Enemy';
import { Projectile } from '../Projectile';
import { BuildingSlot, ChainUpgradeEffect, TargetPriority } from '../../types';
import { selectByPriority } from '../units/BaseUnit';
import { WALL_X, WALL_WIDTH } from '../../config';

export class Crossbow {
    scene: Phaser.Scene;
    x: number;
    y: number;
    level: number;
    position: BuildingSlot;
    unitType: string = 'crossbow';
    graphics: Phaser.GameObjects.Graphics;
    sprite: Phaser.GameObjects.Sprite;
    private attackTimer: number = 0;
    private projectiles: Projectile[];
    stackCount: number = 1;

    // Chain upgrade bonuses
    bonusDamageMult: number = 1;
    attackIntervalMult: number = 1;
    bonusPierceCount: number = 0;
    globalDamageMult: number = 1;
    attackSpeedMult: number = 1;

    constructor(
        scene: Phaser.Scene,
        x: number,
        y: number,
        position: BuildingSlot,
        projectiles: Projectile[]
    ) {
        this.scene = scene;
        this.x = x;
        this.y = y;
        this.level = 1;
        this.position = position;
        this.graphics = scene.add.graphics();
        this.sprite = scene.add.sprite(x, y, 'building_crossbow');
        this.sprite.setOrigin(0.5, 110 / 128);
        this.projectiles = projectiles;
        this.stackCount = 1;
        this.draw();
    }

    getDamage(): number {
        return CROSSBOW.baseDamage * this.stackCount * this.bonusDamageMult * this.globalDamageMult;
    }

    get pierceCount(): number {
        return CROSSBOW.basePierceCount + this.bonusPierceCount;
    }

    addStack(): void {
        this.stackCount++;
    }

    applyChainUpgrade(effect: ChainUpgradeEffect): void {
        if (effect.damageMultBonus !== undefined) this.bonusDamageMult *= (1 + effect.damageMultBonus);
        if (effect.attackIntervalMult !== undefined) this.attackIntervalMult *= effect.attackIntervalMult;
        if (effect.pierceCountBonus !== undefined) this.bonusPierceCount += effect.pierceCountBonus;
        this.level++;
        this.draw();
    }

    update(time: number, delta: number, enemies: Enemy[]): void {
        this.attackTimer -= delta;
        if (this.attackTimer <= 0) {
            const target = this.findTarget(enemies);
            if (target) {
                this.attackTimer = CROSSBOW.attackInterval * this.attackIntervalMult / this.attackSpeedMult;
                this.fire(target);
            }
        }
        this.draw();
    }

    private findTarget(enemies: Enemy[]): Enemy | null {
        const range = CROSSBOW.range;
        const wallEdge = WALL_X + WALL_WIDTH;
        const candidates: Enemy[] = [];
        for (const enemy of enemies) {
            if (enemy.isDead) continue;
            if (enemy.enemyType === 'boulder') continue;
            if (enemy.isBurrowed) continue;
            if (enemy.isGoblin) continue;
            if (enemy.x - wallEdge > range) continue;
            candidates.push(enemy);
        }
        return selectByPriority(candidates, 'highestHP' as TargetPriority, this.x, this.y);
    }

    private fire(target: Enemy): void {
        const proj = new Projectile(this.scene, this.x + 10, this.y - 10, {
            damage: this.getDamage(),
            speed: CROSSBOW.projectileSpeed,
            piercing: this.pierceCount > 0,
            maxPierceCount: this.pierceCount,
            projectileType: 'arrow',
            targetX: target.x,
            targetY: target.y,
        });
        this.projectiles.push(proj);
    }

    draw(): void {
        this.sprite.setTexture('building_crossbow');
        this.graphics.clear();

        if (this.level > 1) {
            this.graphics.fillStyle(0x4488ff, 1);
            for (let i = 0; i < Math.min(this.level - 1, 5); i++) {
                this.graphics.fillCircle(this.x - 6 + i * 3, this.y + 8, 1.5);
            }
        }
    }

    destroy(): void {
        this.graphics.destroy();
        this.sprite.destroy();
    }
}
